import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError, type Connector, type DiscoveredDevice, type Nvr } from "../../api";
import { Modal } from "../../components/Modal";
import { useScope } from "../../components/ScopeContext";
import { Select } from "../../components/Select";
import { BRAND_OPTIONS, Note } from "./shared";

export type RecorderDraft = {
  /** Set when editing a saved recorder. */
  existing?: Nvr;
  name: string;
  brand: string;
  store: string;
  connector_id: string;
  host: string;
  port: number;
  username: string;
  password: string;
};

export function newRecorderDraft(init: Partial<RecorderDraft> = {}): RecorderDraft {
  return {
    name: "",
    brand: "hikvision",
    store: "",
    connector_id: "",
    host: "",
    port: 554,
    username: "",
    password: "",
    ...init,
  };
}

export function editRecorderDraft(n: Nvr): RecorderDraft {
  return newRecorderDraft({
    existing: n,
    name: n.name,
    brand: n.brand,
    store: n.store ?? "",
    connector_id: n.connector_id ?? "",
    host: n.host,
    port: n.port,
    username: n.username,
  });
}

export function draftFromDevice(box: Connector, d: DiscoveredDevice): RecorderDraft {
  return newRecorderDraft({
    name: d.onvif?.name || "",
    brand: BRAND_OPTIONS.some((b) => b.id === d.brand_hint) ? (d.brand_hint as string) : "generic",
    store: box.store,
    connector_id: box.id,
    host: d.ip,
    port: d.rtsp_port || 554,
  });
}

/** Names a box's finds by what they are, numbering look-alikes. */
export function deviceLabels(devices: DiscoveredDevice[]): Map<string, string> {
  const base = devices.map((d) => {
    const brand = BRAND_OPTIONS.find((b) => b.id === d.brand_hint)?.label;
    return d.onvif?.name || d.onvif?.hardware || (brand ? `${brand} recorder` : "Video recorder");
  });
  const seen = new Map<string, number>();
  const totals = new Map<string, number>();
  base.forEach((b) => totals.set(b, (totals.get(b) ?? 0) + 1));
  const out = new Map<string, string>();
  devices.forEach((d, i) => {
    const b = base[i];
    const n = (seen.get(b) ?? 0) + 1;
    seen.set(b, n);
    out.set(d.ip, (totals.get(b) ?? 0) > 1 ? `${b} ${n}` : b);
  });
  return out;
}

function newRecorderId(name: string, brand: string): string {
  const slug = (name || brand || "recorder")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  return `${slug || "recorder"}-${Math.random().toString(36).slice(2, 6)}`;
}

const MANUAL = "__manual";

/** Add or edit a customer's video recorder, always reached through their box. */
export function CustomerRecorderDialog({
  draft: initial,
  onClose,
}: {
  draft: RecorderDraft;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { stores } = useScope();
  const boxes = useQuery({ queryKey: ["connectors"], queryFn: api.connectors.list });
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const boxesHere = (boxes.data ?? []).filter((b) => b.store === draft.store);
  const box = boxesHere.find((b) => b.id === draft.connector_id);
  const found = (box?.discovered?.devices ?? []).filter((d) => d.rtsp_port);
  const labels = deviceLabels(found);
  const [manualChoice, setManual] = useState<boolean | null>(null);
  const manual =
    manualChoice ?? (Boolean(draft.host) && !found.some((d) => d.ip === draft.host));
  const pick = manual ? MANUAL : found.some((d) => d.ip === draft.host) ? draft.host : "";

  const setStore = (store: string) => {
    const here = (boxes.data ?? []).filter((b) => b.store === store);
    setManual(null);
    setDraft({
      ...draft,
      store,
      connector_id: here.length === 1 ? here[0].id : "",
      host: "",
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        id: draft.existing?.id ?? newRecorderId(draft.name, draft.brand),
        name: draft.name.trim(),
        brand: draft.brand,
        host: draft.host.trim(),
        port: draft.port,
        username: draft.username.trim(),
        password: draft.existing && !draft.password ? null : draft.password,
        rtsp_url_template: draft.existing?.rtsp_url_template ?? null,
        store: draft.store,
        connector_id: draft.connector_id || null,
      };
      return draft.existing ? api.nvrs.update(body) : api.nvrs.upsert(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["nvrs"] });
      qc.invalidateQueries({ queryKey: ["stores"] });
      qc.invalidateQueries({ queryKey: ["connectors"] });
      qc.invalidateQueries({ queryKey: ["cameras"] });
      onClose();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : "Could not save the video recorder."),
  });

  const ready = draft.store && draft.connector_id && draft.host.trim();

  return (
    <Modal onClose={onClose} labelledBy="recorder-title" panelClassName="w-full max-w-lg">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <h3 id="recorder-title" className="text-lg font-medium text-white">
          {draft.existing ? "Edit video recorder" : "Add a video recorder"}
        </h3>
        {error && <Note tone="error">{error}</Note>}

        <label className="block">
          <span className="mb-1 block text-xs text-bf-muted">Name</span>
          <input
            className="bf-input"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Shop floor recorder"
            autoFocus
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1 block text-xs text-bf-muted">Location</span>
            <Select
              ariaLabel="Location"
              value={draft.store}
              onChange={setStore}
              placeholder="Choose a location…"
              options={stores.map((s) => ({ value: s.name, label: s.display_name || s.name }))}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs text-bf-muted">Brand</span>
            <Select
              ariaLabel="Brand"
              value={draft.brand}
              onChange={(v) => setDraft({ ...draft, brand: v })}
              options={BRAND_OPTIONS.map((b) => ({ value: b.id, label: b.label }))}
            />
          </div>
        </div>

        {draft.store && boxesHere.length === 0 ? (
          <Note tone="warning">
            This location has no IntraScene box yet. Add your box first; the
            video recorder is connected through it.
          </Note>
        ) : (
          draft.store && (
            <>
              {boxesHere.length > 1 && (
                <div>
                  <span className="mb-1 block text-xs text-bf-muted">Connected through</span>
                  <Select
                    ariaLabel="Connected through"
                    value={draft.connector_id}
                    onChange={(v) => {
                      setManual(null);
                      setDraft({ ...draft, connector_id: v, host: "" });
                    }}
                    placeholder="Choose an IntraScene box…"
                    options={boxesHere.map((b) => ({
                      value: b.id,
                      label: b.name || `IntraScene box ${b.serial}`,
                    }))}
                  />
                </div>
              )}
              {draft.connector_id && (
                <div>
                  <span className="mb-1 block text-xs text-bf-muted">Which recorder</span>
                  <Select
                    ariaLabel="Which recorder"
                    value={pick}
                    onChange={(v) => {
                      if (v === MANUAL) {
                        setManual(true);
                        return;
                      }
                      const d = found.find((x) => x.ip === v);
                      setManual(false);
                      if (!d) return;
                      setDraft({
                        ...draft,
                        host: d.ip,
                        port: d.rtsp_port || 554,
                        name: draft.name || d.onvif?.name || "",
                        brand: BRAND_OPTIONS.some((b) => b.id === d.brand_hint)
                          ? (d.brand_hint as string)
                          : draft.brand,
                      });
                    }}
                    placeholder={
                      found.length > 0 ? "Choose one your box found…" : "Your box has not found any yet"
                    }
                    options={[
                      ...found.map((d) => ({ value: d.ip, label: labels.get(d.ip) ?? d.ip })),
                      { value: MANUAL, label: "It is not in this list" },
                    ]}
                  />
                </div>
              )}
              {manual && (
                <label className="block">
                  <span className="mb-1 block text-xs text-bf-muted">
                    Recorder address on your network
                  </span>
                  <input
                    className="bf-input font-mono text-xs"
                    value={draft.host}
                    onChange={(e) => setDraft({ ...draft, host: e.target.value })}
                    placeholder="192.168.1.50"
                  />
                  <span className="mt-1 block text-xs text-bf-muted">
                    Shown in the recorder's own network settings.
                  </span>
                </label>
              )}
            </>
          )
        )}

        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="mb-1 block text-xs text-bf-muted">Recorder username</span>
            <input
              className="bf-input"
              value={draft.username}
              onChange={(e) => setDraft({ ...draft, username: e.target.value })}
              autoComplete="off"
            />
          </label>
          <label>
            <span className="mb-1 block text-xs text-bf-muted">Recorder password</span>
            <input
              className="bf-input"
              type="password"
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
              autoComplete="new-password"
              placeholder={draft.existing?.password_set ? "Unchanged" : ""}
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className="bf-button disabled:opacity-40"
            type="submit"
            disabled={save.isPending || !ready}
          >
            {save.isPending ? "Saving…" : draft.existing ? "Save" : "Add video recorder"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
